// Survey Explorer API client
// Talks to the local Express proxy at /api/explorer/*,
// which forwards to sam-dbam. This avoids CORS and keeps
// sam-dbam URL server-side.

import { mapQuestionTypeToMatrixType } from './matrixTypes';
import { noteAuthFailure } from './cloudApi';

// ---- Internal helpers ----

const API_BASE = '/api';

// User/org identity for sam-dbam's AuthStub middleware. dbam reads these
// off X-User-ID / X-Org-ID request headers (every write that touches audit
// columns like archived_by needs them or it crashes on the ::uuid cast).
//
// Identity now comes from the Hanko-authenticated user (see AuthContext),
// NOT from URL query params — those were trivially forgeable (anyone with
// the Explorer URL could impersonate any user_id). AuthContext calls
// setAuthIdentity() once it has the user from /dbam/v1/me.
//
// Legacy fallback: if AuthContext hasn't set identity yet (e.g. very early
// pre-render call), fall back to URL params so we don't regress dev tools
// or scripts that still rely on the old query-string convention. Remove
// the fallback once the Console handoff is fully migrated.
let _authIdentity = { userId: '', orgId: '' };

export function setAuthIdentity({ userId = '', orgId = '' } = {}) {
    _authIdentity = { userId: userId || '', orgId: orgId || '' };
}

export function getAuthHeaders() {
    const headers = {};
    if (_authIdentity.userId) headers['X-User-ID'] = _authIdentity.userId;
    if (_authIdentity.orgId) headers['X-Org-ID'] = _authIdentity.orgId;
    if (headers['X-User-ID'] || headers['X-Org-ID']) return headers;

    // Legacy fallback — see comment above.
    if (typeof window === 'undefined' || !window.location) return {};
    try {
        const params = new URLSearchParams(window.location.search);
        const userId = params.get('userId');
        const orgId = params.get('orgId');
        if (userId) headers['X-User-ID'] = userId;
        if (orgId) headers['X-Org-ID'] = orgId;
        return headers;
    } catch (_) {
        return {};
    }
}

async function httpRequest(url, options = {}, { timeoutMs = 15000, responseType = 'json' } = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        // Caller-supplied headers win over auth defaults (so an explicit
        // Content-Type or override stays), but the common case is just
        // auth headers + maybe Content-Type for POST/PUT/PATCH bodies.
        const mergedHeaders = { ...getAuthHeaders(), ...(options.headers || {}) };
        const resp = await fetch(url, { ...options, headers: mergedHeaders, signal: controller.signal });
        if (!resp.ok) {
            noteAuthFailure(resp);
            let errText = '';
            try { errText = await resp.text(); } catch (_) { }
            let errorMessage = errText || resp.statusText || 'Request failed';
            let errorCode = '';
            let errorPayload = null;
            if (errText) {
                try {
                    errorPayload = JSON.parse(errText);
                    const returnedError = errorPayload?.error;
                    if (typeof returnedError === 'string' && returnedError) {
                        errorMessage = returnedError;
                    } else if (returnedError && typeof returnedError === 'object') {
                        errorMessage = returnedError.message || errorMessage;
                        errorCode = returnedError.code || '';
                    }
                } catch (_) {
                    // Preserve non-JSON response text as the actionable message.
                }
            }
            const error = new Error(errorMessage);
            error.status = resp.status;
            if (errorCode) error.code = errorCode;
            if (errorPayload) error.payload = errorPayload;
            throw error;
        }
        if (responseType === 'json') return await resp.json();
        return await resp.text();
    } catch (err) {
        if (err && (err.name === 'AbortError' || /aborted/i.test(String(err.message)))) {
            const timeoutError = new Error(`Request timed out after ${timeoutMs} ms`);
            timeoutError.code = 'ETIMEOUT';
            throw timeoutError;
        }
        throw err;
    } finally {
        clearTimeout(timer);
    }
}

function unwrap(resp) {
    if (!resp || typeof resp !== 'object') return resp;
    if (resp.data !== undefined) {
        const inner = resp.data;
        // A null/undefined inner is the empty-list case from a backend that
        // returned Go nil for a slice. Surface it as [] so callers using
        // .map / .forEach don't blow up — preserves the contract whether
        // the backend later normalizes to [] or stays inconsistent.
        if (inner === null || inner === undefined) return [];
        // Legacy double-wrap shape: {"data": {"data": [...]}}. Unwrap one
        // more layer if the inner.data is itself a list (or null, same
        // empty-list treatment as above).
        if (typeof inner === 'object' && !Array.isArray(inner) && 'data' in inner) {
            if (inner.data === null || inner.data === undefined) return [];
            if (Array.isArray(inner.data)) return inner.data;
        }
        return inner;
    }
    return resp;
}

// ---- Explorer API functions ----

/**
 * Fetch aggregated survey responses for a deployment.
 * Proxied: /api/explorer/responses/{deploymentId}
 */
export async function fetchExplorerResponses(deploymentId, { limit = 200, offset = 0, since } = {}) {
    let url = `${API_BASE}/explorer/responses/${deploymentId}?limit=${limit}&offset=${offset}`;
    if (since) url += `&since=${encodeURIComponent(since)}`;
    const resp = await httpRequest(url, {}, { timeoutMs: 20000 });
    return unwrap(resp);
}

/**
 * Fetch survey structure (questions/variables) parsed from the descriptor.
 * Proxied: /api/explorer/structure/{surveyId}
 */
export async function fetchSurveyStructure(surveyId, deploymentId = '') {
    const suffix = deploymentId ? `?deployment_id=${encodeURIComponent(deploymentId)}` : '';
    const resp = await httpRequest(`${API_BASE}/explorer/structure/${surveyId}${suffix}`);
    return unwrap(resp);
}

/**
 * Fetch all upload metadata visible to the authenticated organization for a
 * deployment. The server returns { files }, but accepts the normal Explorer
 * auth headers supplied by httpRequest.
 */
export async function fetchDeploymentFileUploads(deploymentId) {
    if (!deploymentId) return [];
    const resp = await httpRequest(
        `${API_BASE}/file-uploads?deployment_id=${encodeURIComponent(deploymentId)}`,
        {},
        { timeoutMs: 20000 }
    );
    const data = unwrap(resp);
    if (Array.isArray(data)) return data;
    return Array.isArray(data?.files) ? data.files : [];
}

/**
 * Fetch the survey's response map (WP-SE-32).
 * Returns the full map or null if none exists (404).
 */
export async function fetchResponseMapping(surveyId) {
    try {
        const resp = await httpRequest(`${API_BASE}/explorer/mappings/${surveyId}`);
        return unwrap(resp);
    } catch (e) {
        // 404 means no map saved yet — return null, not an error
        if (e && e.message && e.message.includes('404')) return null;
        throw e;
    }
}

/**
 * Create or update the survey's response map (WP-SE-32).
 */
export async function saveResponseMapping({ surveyId, name, configuration }) {
    const resp = await httpRequest(`${API_BASE}/explorer/mappings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            survey_id: surveyId,
            name,
            configuration,
        }),
    });
    return unwrap(resp);
}

/**
 * Delete a response map.
 */
export async function deleteResponseMapping(mappingId) {
    await httpRequest(`${API_BASE}/explorer/mappings/${mappingId}`, {
        method: 'DELETE',
    }, { responseType: 'text' });
}

/**
 * Submit responses for AI grouping analysis.
 */
export async function analyzeResponses(responses, context = '') {
    const resp = await httpRequest(`${API_BASE}/explorer/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ responses, context }),
    });
    return unwrap(resp);
}

// ---- Surveys & Deployments ----

/**
 * Fetch all surveys.
 * Proxied: /api/surveys
 */
export async function fetchSurveys() {
    const resp = await httpRequest(`${API_BASE}/surveys`);
    return unwrap(resp);
}

/**
 * Fetch deployments for a survey.
 * Proxied: /api/surveys (with query param — routed through deployments proxy)
 */
export async function fetchDeployments(surveyId) {
    const resp = await httpRequest(`${API_BASE}/deployments?survey_id=${surveyId}`);
    return unwrap(resp);
}

// Cross-survey Design/Analysis selectors must be complete: the basic
// deployments endpoint defaults to a 50-row page. Keep fetchDeployments()
// backward-compatible and use this explicit exhaustive helper where a user is
// choosing from the full durable roster history.
export async function fetchAllDeployments(surveyId, { limit = 100 } = {}) {
    const pageSize = Math.max(1, Math.min(100, Number(limit) || 100));
    const all = [];
    let offset = 0;
    let total = null;
    for (let page = 0; page < 1000; page += 1) {
        const raw = await httpRequest(`${API_BASE}/deployments?survey_id=${encodeURIComponent(surveyId)}&limit=${pageSize}&offset=${offset}`);
        const rows = Array.isArray(raw?.data) ? raw.data : unwrap(raw);
        const list = Array.isArray(rows) ? rows : [];
        all.push(...list);
        const reported = raw?.total_count ?? raw?.meta?.total;
        if (Number.isFinite(Number(reported))) total = Number(reported);
        offset += list.length;
        if (list.length === 0 || (total !== null && offset >= total) || (total === null && list.length < pageSize)) break;
    }
    return [...new Map(all.map(row => [row.deployment_id || row.id, row])).values()];
}

/**
 * Fetch enriched deployments for a survey (includes funnel counts).
 * Proxied via /api/explorer → sam-dbam /dbam/v1/explorer/deployments/{surveyId}
 */
export async function fetchEnrichedDeployments(surveyId) {
    const resp = await httpRequest(`${API_BASE}/explorer/deployments/${surveyId}`);
    return unwrap(resp);
}

/**
 * Fetch flat per-row results for a deployment.
 * Returns rows: { id, recipient_id, variable_name, value_json, answer_text,
 *   confidence, delegated_to, email, created_at }.
 * Proxied: /api/deployments/{deploymentId}/results
 *
 * NOTE: This endpoint does NOT carry session_id, completion_status,
 * response_date, or per-session confidence_weight. For the explorer's
 * session-level views, use fetchExplorerResponses / loadDeploymentResponses
 * (which target /dbam/v1/explorer/responses) instead. Retained here for
 * ad-hoc debugging and any external caller; not used by the explorer UI.
 */
export async function fetchDeploymentResults(deploymentId, { limit = 500, offset = 0 } = {}) {
    const resp = await httpRequest(
        `${API_BASE}/deployments/${deploymentId}/results?limit=${limit}&offset=${offset}`,
        {},
        { timeoutMs: 20000 }
    );
    return unwrap(resp);
}

/**
 * Transform raw analyzer_events into exchange-shaped objects for the Timeline view.
 * Ported from console/src/utils/cloudApi.js (per WP-SE-33 D33.4).
 * Groups events into human turns and extracts human/AI content from payloads.
 *
 * Older session blobs did not preserve the kernel's exchange counter when a
 * request moved between instances. That produced repeated exchange_seq values
 * and the old object-by-sequence grouping merged unrelated questions together.
 * A second human event for the same raw sequence now starts a new logical turn,
 * allowing already-persisted timelines to render correctly.
 */
export function eventsToExchanges(events) {
    if (!Array.isArray(events) || events.length === 0) return { exchanges: [], prompts: {} };
    const groups = [];
    const activeByRawSequence = new Map();
    const humanClasses = new Set(['human', 'human_utterance', 'response', 'asr_result', 'asr']);

    const createGroup = (ev) => {
        const group = {
            exchange_seq: 0,
            raw_exchange_seq: ev.exchange_seq ?? 0,
            question_id: ev.question_id || null,
            human: [],
            ai_responses: [],
            prompts: [],
        };
        groups.push(group);
        activeByRawSequence.set(group.raw_exchange_seq, group);
        return group;
    };

    for (const ev of events) {
        const rawSequence = ev.exchange_seq ?? 0;
        const cls = String(ev.class || '').toLowerCase();
        const payload = ev.payload || {};
        let group = activeByRawSequence.get(rawSequence);

        // Repeated human input under one raw sequence marks a rehydration-reset
        // collision. Split it into a new turn rather than concatenating answers
        // from different questions into one Recipient cell.
        if (!group || (humanClasses.has(cls) && group.human.length > 0)) {
            group = createGroup(ev);
        }

        if (humanClasses.has(cls)) {
            const text = payload.text || payload.utterance || payload.content || payload.raw || (typeof payload === 'string' ? payload : '');
            if (text) group.human.push(String(text));
        } else if (cls === 'ai' || cls === 'ai_response' || cls === 'sam' || cls === 'conversational') {
            group.ai_responses.push({
                text: payload.text || payload.utterance || payload.content || payload.conversationalResponse || '',
                type: payload.type || payload.evaluationType || null,
                nextInstruction: payload.nextInstruction || null,
                shouldAdvanceSurvey: payload.shouldAdvanceSurvey ?? null,
                extractedData: payload.extractedData || null,
            });
        } else if (cls === 'nap' || cls === 'normalized_ai_payload') {
            group.ai_responses.push({
                text: payload.conversationalResponse || payload.text || '',
                type: payload.evaluationType || payload.type || null,
                nextInstruction: payload.nextInstruction || null,
                shouldAdvanceSurvey: payload.shouldAdvanceSurvey ?? null,
                extractedData: payload.extractedData || null,
            });
        } else if (cls === 'ai_prompt') {
            group.prompts.push({
                exchange_seq: rawSequence,
                prompt_type: payload.prompt_type || 'hybrid',
                bytes: payload.bytes || (payload.prompt_text ? payload.prompt_text.length : 0),
                prompt_text: payload.prompt_text || '',
            });
        }
        if (ev.question_id && !group.question_id) group.question_id = ev.question_id;
    }

    const promptsBySeq = {};
    let nextSequence = 1;
    groups.forEach((group, index) => {
        // A leading utterance with no question is volunteered context and belongs
        // to sequence 0. Authored question/answer turns start at sequence 1.
        const volunteeredBeforeQuestion = index === 0 && !group.question_id && group.human.length > 0;
        group.exchange_seq = volunteeredBeforeQuestion ? 0 : nextSequence++;
        group.prompts = group.prompts.map(prompt => ({
            ...prompt,
            exchange_seq: group.exchange_seq,
        }));
        if (group.prompts.length > 0) promptsBySeq[group.exchange_seq] = group.prompts;
    });

    return {
        exchanges: groups,
        prompts: promptsBySeq,
    };
}

/**
 * Fetch SRA sessions for a survey, optionally scoped to a deployment.
 * Proxied: /api/surveys/{surveyId}/sessions → /dbam/v1/surveys/{surveyId}/sessions
 * The dbam route filters by deployment_id server-side (COALESCE of the session's
 * own deployment_id and the legacy recipients bridge).
 */
export async function fetchExplorerSessions(surveyId, deploymentId, { limit = 500, offset = 0 } = {}) {
    if (!surveyId) return [];
    try {
        let url = `${API_BASE}/surveys/${encodeURIComponent(surveyId)}/sessions?limit=${limit}&offset=${offset}`;
        if (deploymentId) url += `&deployment_id=${encodeURIComponent(deploymentId)}`;
        const resp = await httpRequest(url, {}, { timeoutMs: 20000 });
        const rows = unwrap(resp);
        return Array.isArray(rows) ? rows : [];
    } catch (e) {
        if (e?.status === 404) return [];
        throw e;
    }
}

/**
 * Permanently delete one response take/session and all of its session-owned
 * database rows. The result always includes blob_cleanup: `complete` means
 * recorded audio and uploaded files were removed too; `partial` or
 * `not_configured` means the take is gone from the database but the UI must
 * show the returned cleanup warning rather than claim full object cleanup.
 *
 * Proxied: DELETE /api/sessions/{sessionId} → /dbam/v1/sessions/{sessionId}
 */
export async function deleteExplorerSession(sessionId) {
    if (!sessionId) throw new Error('sessionId is required');
    const resp = await httpRequest(
        `${API_BASE}/sessions/${encodeURIComponent(sessionId)}`,
        { method: 'DELETE' },
        { timeoutMs: 30000 }
    );
    return unwrap(resp);
}

/**
 * Fetch SRA exchanges for one session, normalized to { exchanges, prompts }.
 * Proxied: /api/sessions/{sessionId}/analyzer-events → /dbam/v1/sessions/{sessionId}/analyzer-events
 */
export async function fetchExplorerExchanges(sessionId) {
    if (!sessionId) return { exchanges: [], prompts: {} };
    try {
        const resp = await httpRequest(
            `${API_BASE}/sessions/${encodeURIComponent(sessionId)}/analyzer-events`,
            {},
            { timeoutMs: 20000 }
        );
        const events = unwrap(resp);
        return eventsToExchanges(Array.isArray(events) ? events : []);
    } catch (e) {
        if (e?.status === 404) return { exchanges: [], prompts: {} };
        throw e;
    }
}

/**
 * Fetch flat SRA results rows for a deployment.
 * Proxied: /api/deployments/{deploymentId}/results
 *
 * This is the original Console SRA endpoint. It returns per-variable rows
 * ({ recipient_id, variable_name, value_json, answer_text, confidence, email,
 *   created_at, ... }) — the shape RecipientResultsTable was built around.
 *
 * Previously this routed to /explorer/responses/{deploymentID}, which returns
 * session-level aggregated rows with a `response_data` JSONB bundle. That shape
 * does not match RecipientResultsTable's expectations, so the Results pane
 * rendered blank cells. Reverted to the original Console SRA endpoint per the
 * "reduce, don't increase complexity" principle — no React component changes,
 * no sam-dbam changes, only this fetch is repointed.
 */
export async function fetchExplorerResults(deploymentId) {
    if (!deploymentId) return [];
    const resp = await httpRequest(
        `${API_BASE}/deployments/${encodeURIComponent(deploymentId)}/results`,
        {},
        { timeoutMs: 20000 }
    );
    const data = unwrap(resp);
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.results)) return data.results;
    if (data && Array.isArray(data.rows)) return data.rows;
    if (data && Array.isArray(data.responses)) return data.responses;
    return [];
}

/**
 * Fetch the session-keyed per-answer rows for one session.
 * Proxied: /api/sessions/{sessionId}/responses → /dbam/v1/sessions/{sessionId}/responses
 * (SessionHandler.ListResponses → response_repository.ListBySession).
 *
 * This is the session-scoped counterpart to the recipient-keyed
 * fetchExplorerResults. Anonymous/browser takes write no `results` rows, so
 * the Results view falls back to these `responses` rows for the selected
 * session. Mirrors fetchExplorerExchanges' /api/sessions/* passthrough.
 *
 * Row shape (sam-dbam models.Response): { id, session_id, survey_id,
 *   question_id, timestamp, response_text, response_value, confidence,
 *   source, context_notes, follow_up_asked, validation_passed,
 *   audio_storage_path }.
 */
export async function fetchSessionResponses(sessionId) {
    if (!sessionId) return [];
    try {
        const resp = await httpRequest(
            `${API_BASE}/sessions/${encodeURIComponent(sessionId)}/responses`,
            {},
            { timeoutMs: 20000 }
        );
        const data = unwrap(resp);
        return Array.isArray(data) ? data : [];
    } catch (e) {
        if (e?.status === 404) return [];
        throw e;
    }
}

/**
 * Adapt session-keyed `responses` rows into the recipient-keyed shape that
 * RecipientResultsTable (ExploreTab) renders. Only the fields that component
 * reads are mapped; everything else passes through untouched.
 *
 *   variable_name ← question_id   (the kernel writes the survey's variable here)
 *   value_json    ← JSON.parse(response_value), falling back to the raw string
 *                   when it isn't JSON (renderValue handles both)
 *   created_at    ← timestamp
 *   session_id / confidence / audio_storage_path pass through unchanged, so the
 *   existing session_id filter branch matches and audio playback keeps working.
 */
export function adaptSessionResponsesForResults(rows) {
    if (!Array.isArray(rows)) return [];
    return rows.map(r => {
        let valueJson = r.response_value;
        if (typeof r.response_value === 'string') {
            try { valueJson = JSON.parse(r.response_value); } catch (_) { valueJson = r.response_value; }
        }
        return {
            id: r.id,
            session_id: r.session_id,
            variable_name: r.question_id,
            value_json: valueJson,
            created_at: r.timestamp,
            confidence: r.confidence,
            audio_storage_path: r.audio_storage_path,
        };
    });
}

// ---- Data adapters ----
// mapQuestionTypeToMatrixType lives in ./matrixTypes (imported at top).

/**
 * Adapt session-level explorer responses into the shape SurveyExplorer expects.
 *
 * Source endpoint: /dbam/v1/explorer/responses/{deploymentId}
 * Source shape per item (see sam-dbam ResponseItem):
 *   { session_id, recipient_id, recipient_email, recipient_name,
 *     recipient_group, response_date, completion_time,
 *     completion_status, channel_type, response_data: {var→value}, recipient_tags,
 *     confidence_weight }
 *
 * Emits unprefixed top-level keys so downstream consumers such as
 * StatisticalSummary, processChartData, exportUtils, and SurveyExplorer's
 * response merge can read them directly.
 *
 * Variable values from response_data are spread at top level so dimension axes
 * (drag a variable onto X/Y) can read r[varName] directly.
 */
export function adaptResponsesForExplorer(apiResponses) {
    if (!Array.isArray(apiResponses)) return [];
    return apiResponses.map(r => ({
        // Spread variable values first so metadata fields below cannot be
        // shadowed by a survey question whose variable_name collides.
        ...(r.response_data || {}),
        // Recording paths are response metadata, never survey variables.
        // Consumers look up `audio_by_variable[varName]` for an optional play
        // control beside that response.
        audio_by_variable: r.audio_data || {},
        session_id: r.session_id,
        recipient_id: r.recipient_id,
        // How the recipient is NAMED for an author. Every other Explorer
        // screen shows the email; without it here an Analysis surface can
        // only print the recipient_id UUID, which nothing else displays.
        recipient_email: r.recipient_email || null,
        recipient_name: r.recipient_name || null,
        recipient_group: r.recipient_group || null,
        response_date: r.response_date,
        // Server emits these under different JSON names — normalize here
        // so every downstream consumer can read a single canonical shape.
        // sam-dbam ResponseItem: `completion_time_seconds` and `status`.
        completion_time: r.completion_time_seconds,
        completion_status: r.status,
        channel_type: r.channel_type,
        confidence_weight: r.confidence_weight,
        recipient_tags: r.recipient_tags || [],
    }));
}

/**
 * Derive a fallback variables list from adapted responses when the survey
 * structure endpoint returns no questions. Scans response_data keys (the
 * variable names spread at top level) and skips reserved metadata fields.
 */
const RESERVED_FIELDS = new Set([
    'session_id', 'recipient_id', 'recipient_email', 'recipient_name',
    'recipient_group', 'response_date',
    'completion_time', 'completion_status', 'channel_type',
    'confidence_weight', 'recipient_tags', 'audio_by_variable',
]);

export function deriveVariablesFromResponses(responses) {
    if (!Array.isArray(responses) || responses.length === 0) return [];
    const names = new Set();
    responses.forEach(r => {
        Object.keys(r).forEach(k => {
            if (!RESERVED_FIELDS.has(k)) names.add(k);
        });
    });
    return Array.from(names).map(name => {
        const guessedType = guessVariableType(responses, name);
        return {
            name,
            label: name,
            type: guessedType,
            matrixType: mapQuestionTypeToMatrixType(guessedType),
            options: extractOptions(responses, name),
        };
    });
}

/**
 * Load and adapt the deployment's session-level responses in one call.
 * SurveyExplorer uses this directly for both initial load and incremental
 * polling (the `since` cursor is the canonical max(response_date)).
 *
 * Returns { responses, variables, totalCount } where `variables` is a
 * fallback list derived from the data — caller should prefer the survey
 * structure endpoint's questions when available.
 */
export async function loadDeploymentResponses(deploymentId, { since, limit = 200, offset = 0 } = {}) {
    const raw = await fetchExplorerResponses(deploymentId, { since, limit, offset });
    // The endpoint returns { deployment_id, responses, total_count, limit, offset }.
    // unwrap() may return either that envelope or the responses array directly,
    // depending on whether the proxy wraps in { data: ... }.
    let items;
    let totalCount = null;
    if (Array.isArray(raw)) {
        items = raw;
    } else if (raw && Array.isArray(raw.responses)) {
        items = raw.responses;
        totalCount = raw.total_count ?? null;
    } else {
        items = [];
    }
    const responses = adaptResponsesForExplorer(items);
    const variables = deriveVariablesFromResponses(responses);
    return { responses, variables, totalCount };
}

/**
 * Guess the variable type from actual response values.
 */
function guessVariableType(responses, varName) {
    const values = responses.map(r => r[varName]).filter(v => v != null);
    if (values.length === 0) return 'text';

    const allNumeric = values.every(v => !isNaN(parseFloat(v)));
    if (allNumeric) return 'number';

    const uniqueValues = new Set(values);
    if (uniqueValues.size <= 10) return 'categorical';

    return 'text';
}

/**
 * Extract unique option values for a variable.
 */
function extractOptions(responses, varName) {
    const values = responses.map(r => r[varName]).filter(v => v != null);
    return [...new Set(values)].sort();
}

/**
 * Fetch performance metrics for a deployment.
 * Proxied via /api/explorer → sam-dbam /dbam/v1/explorer/performance/{deploymentId}
 */
export async function fetchPerformance(deploymentId) {
    const resp = await httpRequest(`${API_BASE}/explorer/performance/${deploymentId}`);
    return unwrap(resp);
}

/**
 * List surveys that can be archived right now (and those that can't, with a
 * reason). The UI uses this to render the multi-select list — non-archivable
 * rows are visible but disabled so users never select something the backend
 * would reject.
 *
 * Proxied: /api/surveys/archivable → sam-dbam /dbam/v1/surveys/archivable
 */
export async function fetchArchivableSurveys() {
    const resp = await httpRequest(`${API_BASE}/surveys/archivable`);
    return unwrap(resp);
}

/**
 * PREPARE phase of the two-phase survey archive. Builds the .samarchive
 * server-side and streams it back. The response carries the operation_id
 * and the server-computed manifest checksum in headers; the caller MUST
 * hash the received blob and POST the matching checksum to
 * confirmSurveyArchive(...) — until that confirm lands, no live data has
 * been purged. If the caller never confirms, the pending operation
 * expires on the server (TTL ~30 min) and the temp file is cleaned up.
 *
 * Returns { blob, filename, operationId, checksum, expiresAt }.
 *
 * Errors (status-coded so the UI can react):
 *   422 NOT_ARCHIVABLE       — survey has open deployments or in-flight sessions
 *   409 ALREADY_ARCHIVED     — survey is already retired
 *   403 FORBIDDEN            — wrong org
 *   404 NOT_FOUND            — survey doesn't exist
 *
 * Proxied: POST /api/surveys/{id}/archive/prepare
 */
export async function prepareSurveyArchive(surveyId) {
    const response = await fetch(`${API_BASE}/surveys/${surveyId}/archive/prepare`, {
        method: 'POST',
        headers: getAuthHeaders(),
    });
    if (!response.ok) {
        const { message, parsed } = await extractErrorMessage(response, 'Prepare failed');
        const error = new Error(message);
        error.status = response.status;
        error.data = parsed;
        throw error;
    }
    const checksum = response.headers.get('X-Manifest-Checksum') || response.headers.get('x-manifest-checksum');
    const operationId = response.headers.get('X-Archive-Operation-Id') || response.headers.get('x-archive-operation-id');
    const expiresAt = response.headers.get('X-Archive-Expires-At') || response.headers.get('x-archive-expires-at');
    const contentDisposition = response.headers.get('Content-Disposition') || response.headers.get('content-disposition');
    let filename = `survey_${surveyId}.samarchive`;
    if (contentDisposition) {
        const match = contentDisposition.match(/filename="([^"]+)"/);
        if (match && match[1]) filename = match[1];
    }
    const blob = await response.blob();
    return { blob, filename, operationId, checksum, expiresAt };
}

/**
 * CONFIRM phase. Client computes SHA-256 of the received .samarchive bytes,
 * POSTs the hex digest back. On match the server runs the purge transaction.
 * On mismatch the server returns 422 and nothing is purged.
 *
 * Idempotent — a duplicate confirm-POST sees the pending row already gone
 * and gets 404, which the caller can treat as "already confirmed."
 *
 * Proxied: POST /api/surveys/archive-operations/{operationId}/confirm
 */
export async function confirmSurveyArchive(operationId, clientChecksum) {
    const response = await fetch(`${API_BASE}/surveys/archive-operations/${operationId}/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ client_checksum: clientChecksum }),
    });
    if (!response.ok) {
        const { message, parsed } = await extractErrorMessage(response, 'Confirm failed');
        const error = new Error(message);
        error.status = response.status;
        error.data = parsed;
        throw error;
    }
    return response.json();
}

/**
 * Abandon a pending archive operation (e.g., user cancelled mid-download).
 * Idempotent — missing operations succeed silently.
 *
 * Proxied: DELETE /api/surveys/archive-operations/{operationId}
 */
export async function abandonSurveyArchive(operationId) {
    const response = await fetch(`${API_BASE}/surveys/archive-operations/${operationId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
    });
    if (!response.ok) {
        const { message, parsed } = await extractErrorMessage(response, 'Abandon failed');
        const error = new Error(message);
        error.status = response.status;
        error.data = parsed;
        throw error;
    }
}

/**
 * Pull a usable error message out of a non-OK Response. sam-dbam wraps
 * errors as `{"error": {"code": "...", "message": "..."}, "meta": {...}}`
 * — so the previous `parsed.error || ...` chain stringified to
 * `"[object Object]"`. This helper drills into nested shapes and
 * guarantees a string message.
 */
async function extractErrorMessage(response, fallback) {
    let errText = '';
    try { errText = await response.text(); } catch (_) {}
    let parsed = {};
    try { parsed = JSON.parse(errText); } catch (_) {}
    const candidates = [
        // sam-dbam wrapped error: {"error": {"code": "...", "message": "..."}}
        parsed?.error?.message,
        // Some endpoints return {"message": "..."} flat.
        parsed?.message,
        // Or an error string at top level.
        typeof parsed?.error === 'string' ? parsed.error : null,
        // Raw body if it's a plain string.
        errText,
        response.statusText,
        fallback,
    ];
    const message = candidates.find(v => typeof v === 'string' && v.length > 0) || fallback;
    return { message, parsed };
}

/**
 * Hash a Blob's contents as a lowercase SHA-256 hex digest using the
 * browser's WebCrypto. Used by the Archiver's two-phase confirm flow:
 * after receiving the .samarchive from prepareSurveyArchive, the client
 * hashes the bytes and posts the digest to confirmSurveyArchive.
 */
export async function sha256OfBlob(blob) {
    const buf = await blob.arrayBuffer();
    const hashBuf = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(hashBuf))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
}

// ---- Free-text classification (WP-D3) ----
// All paths hang off /api/deployments/{id}/questions/{qid}/... which the
// Express proxy PASSTHROUGH forwards to /dbam/v1/deployments/... — no
// server.js change. Auth headers are applied by httpRequest automatically.

/**
 * Fetch the classification rows for a free-text question.
 * GET /api/deployments/{id}/questions/{qid}/classification
 * Returns an array (unwrap coerces null → []). 404 (never classified) → [].
 */
export async function fetchClassification(deploymentId, qid) {
    try {
        const resp = await httpRequest(
            `${API_BASE}/deployments/${encodeURIComponent(deploymentId)}/questions/${encodeURIComponent(qid)}/classification`,
            {},
            { timeoutMs: 20000 }
        );
        const data = unwrap(resp);
        return Array.isArray(data) ? data : [];
    } catch (e) {
        if (e?.status === 404) return [];
        throw e;
    }
}

/**
 * Kick off a (re-)classification job.
 * POST /api/deployments/{id}/questions/{qid}/classify
 * Returns the job_id.
 */
export async function startClassify(deploymentId, qid) {
    const resp = await httpRequest(
        `${API_BASE}/deployments/${encodeURIComponent(deploymentId)}/questions/${encodeURIComponent(qid)}/classify`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: '{}',
        }
    );
    return unwrap(resp).job_id;
}

/**
 * Poll a classification job's status.
 * GET /api/deployments/{id}/questions/{qid}/classify/jobs/{jobId}
 * Returns { status, progress, ... }.
 */
export async function getClassifyJob(deploymentId, qid, jobId) {
    const resp = await httpRequest(
        `${API_BASE}/deployments/${encodeURIComponent(deploymentId)}/questions/${encodeURIComponent(qid)}/classify/jobs/${encodeURIComponent(jobId)}`
    );
    return unwrap(resp);
}

/**
 * List author labels (review judgments) for a classified question.
 * GET /api/deployments/{id}/questions/{qid}/labels
 * Returns an array. 404 → [].
 */
export async function listLabels(deploymentId, qid) {
    try {
        const resp = await httpRequest(
            `${API_BASE}/deployments/${encodeURIComponent(deploymentId)}/questions/${encodeURIComponent(qid)}/labels`
        );
        const data = unwrap(resp);
        return Array.isArray(data) ? data : [];
    } catch (e) {
        if (e?.status === 404) return [];
        throw e;
    }
}

/**
 * Create (or upsert) an author label resolving one ambiguous response.
 * POST /api/deployments/{id}/questions/{qid}/labels
 * body { session_id, label } — label is a numeric bucket index string or 'other'.
 * WRITES TOUCH LABELS ONLY — never mutate the classification rows.
 */
export async function createLabel(deploymentId, qid, { session_id, label }) {
    const resp = await httpRequest(
        `${API_BASE}/deployments/${encodeURIComponent(deploymentId)}/questions/${encodeURIComponent(qid)}/labels`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_id, label }),
        }
    );
    return unwrap(resp);
}

/**
 * Remove an author label (undo a judgment).
 * DELETE /api/deployments/{id}/questions/{qid}/labels/{sessionId} (204)
 * WRITES TOUCH LABELS ONLY — never mutate the classification rows.
 */
export async function deleteLabel(deploymentId, qid, sessionId) {
    await httpRequest(
        `${API_BASE}/deployments/${encodeURIComponent(deploymentId)}/questions/${encodeURIComponent(qid)}/labels/${encodeURIComponent(sessionId)}`,
        { method: 'DELETE' },
        { responseType: 'text' }
    );
}

// ---- Category discovery + adoption (WP-D3b) ----

/**
 * Kick off a category-discovery job (deterministic embedding clustering — no
 * LLM). POST /api/deployments/{id}/questions/{qid}/discover
 * Returns the job_id; poll it with getClassifyJob (shared jobs table/route).
 */
// scope 'residue' clusters only the answers automatic categorization could not
// place, which is what proposing genuinely new categories needs. Omitting it
// clusters every answer.
export async function startDiscover(deploymentId, qid, scope = '') {
    const suffix = scope ? `?scope=${encodeURIComponent(scope)}` : '';
    const resp = await httpRequest(
        `${API_BASE}/deployments/${encodeURIComponent(deploymentId)}/questions/${encodeURIComponent(qid)}/discover${suffix}`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: '{}',
        }
    );
    return unwrap(resp).job_id;
}

/**
 * Fetch the stored discovery dendrogram for a question.
 * GET /api/deployments/{id}/questions/{qid}/discovery
 * Returns { n, result: { sessions, merges }, ... } or null when discovery has
 * never completed (404).
 */
export async function fetchDiscovery(deploymentId, qid) {
    try {
        const resp = await httpRequest(
            `${API_BASE}/deployments/${encodeURIComponent(deploymentId)}/questions/${encodeURIComponent(qid)}/discovery`,
            {},
            { timeoutMs: 20000 }
        );
        return unwrap(resp) || null;
    } catch (e) {
        if (e?.status === 404) return null;
        throw e;
    }
}

/**
 * Adopt/append response categories for a question (writes into the survey
 * descriptor server-side). APPEND-ONLY once categories exist: the request must
 * list the existing categories (by summary, in order) as a prefix — anything
 * else is a 409 (INDEX-STABILITY INVARIANT, plan v2 §4a D3b.2).
 * PUT /api/deployments/{id}/questions/{qid}/response-categories
 * body { categories: [{ summary, description?, phrasings? }, ...] }
 * Returns the final category list.
 */
export async function putResponseCategories(deploymentId, qid, categories) {
    const resp = await httpRequest(
        `${API_BASE}/deployments/${encodeURIComponent(deploymentId)}/questions/${encodeURIComponent(qid)}/response-categories`,
        {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ categories }),
        }
    );
    return unwrap(resp).categories;
}

/**
 * Delete the response category at `index` for a question. The server splices it
 * out and renumbers the index-keyed derived state (author labels are remapped;
 * cached vectors + classification rows are cleared), so the caller should re-run
 * classification afterwards to repopulate the chart.
 * DELETE /api/deployments/{id}/questions/{qid}/response-categories/{index}
 * Returns the remaining category list.
 */
export async function deleteResponseCategory(deploymentId, qid, index) {
    const resp = await httpRequest(
        `${API_BASE}/deployments/${encodeURIComponent(deploymentId)}/questions/${encodeURIComponent(qid)}/response-categories/${encodeURIComponent(index)}`,
        { method: 'DELETE' }
    );
    return unwrap(resp).categories;
}

// ---- P3 deployment-scoped Design definitions ----
const designURL = (deploymentId, suffix = '') => `${API_BASE}/deployments/${encodeURIComponent(deploymentId)}/design${suffix}`;
export async function fetchDesignRoster(deploymentId) { return unwrap(await httpRequest(designURL(deploymentId, '/roster'))); }
export async function fetchDesignSegmentations(deploymentId) { return unwrap(await httpRequest(designURL(deploymentId, '/segmentations'))); }
export async function fetchDesignExclusions(deploymentId) { return unwrap(await httpRequest(designURL(deploymentId, '/exclusions'))); }
export async function createDesignSegmentation(deploymentId, body) { return unwrap(await httpRequest(designURL(deploymentId, '/segmentations'), { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(body) })); }
export async function updateDesignSegmentation(deploymentId,id,body) { return unwrap(await httpRequest(designURL(deploymentId, `/segmentations/${encodeURIComponent(id)}`), { method:'PUT', headers:{'Content-Type':'application/json'}, body:JSON.stringify(body) })); }
export async function deleteDesignSegmentation(deploymentId,id) { return httpRequest(designURL(deploymentId, `/segmentations/${encodeURIComponent(id)}`), { method:'DELETE' }, {responseType:'text'}); }
export async function createDesignSegment(deploymentId, segmentationId, body) { return unwrap(await httpRequest(designURL(deploymentId, `/segmentations/${encodeURIComponent(segmentationId)}/segments`), { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(body) })); }
export async function updateDesignSegment(deploymentId,id,body) { return unwrap(await httpRequest(designURL(deploymentId, `/segments/${encodeURIComponent(id)}`), { method:'PUT', headers:{'Content-Type':'application/json'}, body:JSON.stringify(body) })); }
export async function deleteDesignSegment(deploymentId,id) { return httpRequest(designURL(deploymentId, `/segments/${encodeURIComponent(id)}`), { method:'DELETE' }, {responseType:'text'}); }
export async function setDesignSegmentMembers(deploymentId, segmentId, recipient_ids) { return unwrap(await httpRequest(designURL(deploymentId, `/segments/${encodeURIComponent(segmentId)}/members`), { method:'PUT', headers:{'Content-Type':'application/json'}, body:JSON.stringify({recipient_ids}) })); }

// ---- L1 organization-wide logical analysis segments ----
// These are intentionally separate from deployment-local Design segment APIs.
// Explorer Design uses this catalog exclusively for analytical segmentation.
const analysisSegmentsURL = (suffix = '') => `${API_BASE}/analysis/segments${suffix}`;
// Analysis V1 is deliberately separate from the legacy logical-Segment CRUD
// above. These endpoints accept only exact deployment-scoped mapping refs.
const analysisV1URL = (suffix = '') => `${API_BASE}/analysis${suffix}`;
export async function searchAnalysisSegmentMappings(body) {
    return unwrap(await httpRequest(analysisV1URL('/segment-mappings/search'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }));
}
export async function queryAnalysisSegmentComparisons(body) {
    return unwrap(await httpRequest(analysisV1URL('/segment-comparisons/query'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }));
}
export async function fetchAnalysisSegments() { return unwrap(await httpRequest(analysisSegmentsURL())); }
export async function createAnalysisSegment(body) { return unwrap(await httpRequest(analysisSegmentsURL(), { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(body) })); }
export async function updateAnalysisSegment(logicalSegmentId, body) { return unwrap(await httpRequest(analysisSegmentsURL(`/${encodeURIComponent(logicalSegmentId)}`), { method:'PUT', headers:{'Content-Type':'application/json'}, body:JSON.stringify(body) })); }
export async function deleteAnalysisSegment(logicalSegmentId) { return unwrap(await httpRequest(analysisSegmentsURL(`/${encodeURIComponent(logicalSegmentId)}`), { method:'DELETE' })); }
// mapping_scope states what the mapping MEANS: 'whole_roster' when the author
// assigned the deployment's entire roster, 'subset' when they chose recipients.
// Compare reads it to tell a cross-deployment A/B arm from a subset, which it
// cannot infer from the member count.
export async function setAnalysisSegmentMembers(logicalSegmentId, deploymentId, recipient_ids, mapping_scope = 'subset') { return unwrap(await httpRequest(analysisSegmentsURL(`/${encodeURIComponent(logicalSegmentId)}/members`), { method:'PUT', headers:{'Content-Type':'application/json'}, body:JSON.stringify({ deployment_id:deploymentId, recipient_ids, mapping_scope }) })); }
export async function createDesignCohort(deploymentId, body) { return unwrap(await httpRequest(designURL(deploymentId, '/cohorts'), { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(body) })); }
export async function updateDesignCohort(deploymentId,id,body) { return unwrap(await httpRequest(designURL(deploymentId, `/cohorts/${encodeURIComponent(id)}`), { method:'PUT', headers:{'Content-Type':'application/json'}, body:JSON.stringify(body) })); }
export async function deleteDesignCohort(deploymentId,id) { return httpRequest(designURL(deploymentId, `/cohorts/${encodeURIComponent(id)}`), { method:'DELETE' }, {responseType:'text'}); }
export async function setDesignCohortMembers(deploymentId, cohortId, recipient_ids) { return unwrap(await httpRequest(designURL(deploymentId, `/cohorts/${encodeURIComponent(cohortId)}/members`), { method:'PUT', headers:{'Content-Type':'application/json'}, body:JSON.stringify({recipient_ids}) })); }
export async function excludeDesignRecipient(deploymentId, recipient_id, reason) { return unwrap(await httpRequest(designURL(deploymentId, '/exclusions'), { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({recipient_id,reason}) })); }
export async function restoreDesignRecipient(deploymentId, recipientId) { return unwrap(await httpRequest(designURL(deploymentId, `/exclusions/${encodeURIComponent(recipientId)}/restore`), { method:'POST' })); }
export async function putDesignCategories(deploymentId, qid, categories) { return unwrap(await httpRequest(designURL(deploymentId, `/categories/${encodeURIComponent(qid)}`), { method:'PUT', headers:{'Content-Type':'application/json'}, body:JSON.stringify({categories}) })); }

/**
 * Trigger deleting of a deployment.
 */
export async function deleteDeployment(deploymentId) {
    const url = `${API_BASE}/deployments/${deploymentId}`;
    const response = await fetch(url, { method: 'DELETE' });
    if (!response.ok) {
        let errText = '';
        try { errText = await response.text(); } catch (_) {}
        let parsed = {};
        try { parsed = JSON.parse(errText); } catch (_) {}
        const error = new Error(parsed.message || parsed.error || errText || response.statusText || 'Delete failed');
        error.status = response.status;
        error.data = parsed;
        throw error;
    }
}

/**
 * Fetch entries from the survey_archives table.
 */
export async function fetchArchives(surveyId) {
    let url = `${API_BASE}/deployments/archives`;
    if (surveyId) url += `?survey_id=${surveyId}`;
    const response = await fetch(url);
    if (!response.ok) {
        let errText = '';
        try { errText = await response.text(); } catch (_) {}
        const error = new Error(errText || response.statusText || 'Failed to fetch archives');
        error.status = response.status;
        throw error;
    }
    return unwrap(await response.json());
}

/**
 * Update the notes field for a survey archive entry.
 */
export async function updateArchiveNotes(archiveId, notes) {
    const url = `${API_BASE}/deployments/archives/${archiveId}/notes`;
    const response = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes }),
    });
    if (!response.ok) {
        let errText = '';
        try { errText = await response.text(); } catch (_) {}
        const error = new Error(errText || response.statusText || 'Failed to update notes');
        error.status = response.status;
        throw error;
    }
    return unwrap(await response.json());
}

/**
 * Restore a survey from a .samarchive file. Uploads via multipart/form-data.
 * Returns the restore summary (table counts, skipped, audio stats).
 * Requires paid tier — free-tier users will receive 403.
 */
export async function restoreSurveyArchive(file) {
    const url = `${API_BASE}/surveys/archive-restore`;
    const formData = new FormData();
    formData.append('archive', file);
    const response = await fetch(url, {
        method: 'POST',
        body: formData,
    });
    if (!response.ok) {
        let errText = '';
        try { errText = await response.text(); } catch (_) {}
        const error = new Error(errText || response.statusText || 'Failed to restore archive');
        error.status = response.status;
        try { error.data = JSON.parse(errText); } catch (_) {}
        throw error;
    }
    return unwrap(await response.json());
}
